import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

// Input Props Interface
interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  variant?: 'default' | 'filled' | 'outlined';
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Input component with multiple variants and states
 * 
 * @param {InputProps} props - Input component props
 * @returns {JSX.Element} Input component
 * 
 * @example
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   error="Email is required"
 * />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  variant = 'default',
  size = 'md',
  className,
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  const baseClasses = 'w-full transition-all duration-300 ease-agentic focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    default: clsx(
      'border border-agentic-gray-300 rounded-md',
      'focus:border-agentic-primary-blue focus:ring-1 focus:ring-agentic-primary-blue',
      'placeholder-agentic-text-muted',
      error && 'border-agentic-accent-red focus:border-agentic-accent-red focus:ring-agentic-accent-red'
    ),
    filled: clsx(
      'bg-agentic-gray-100 border-0 rounded-md',
      'focus:bg-agentic-white focus:ring-2 focus:ring-agentic-primary-blue',
      'placeholder-agentic-text-muted',
      error && 'bg-red-50 focus:bg-agentic-white focus:ring-agentic-accent-red'
    ),
    outlined: clsx(
      'border-2 border-agentic-gray-300 rounded-md bg-transparent',
      'focus:border-agentic-primary-blue focus:ring-0',
      'placeholder-agentic-text-muted',
      error && 'border-agentic-accent-red focus:border-agentic-accent-red'
    ),
  };

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg',
  };

  const iconPadding = {
    sm: leftIcon ? 'pl-10' : rightIcon ? 'pr-10' : '',
    md: leftIcon ? 'pl-12' : rightIcon ? 'pr-12' : '',
    lg: leftIcon ? 'pl-14' : rightIcon ? 'pr-14' : '',
  };

  const inputClasses = clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    iconPadding[size],
    className
  );

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-agentic-text-primary mb-2"
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className="text-agentic-text-muted">
              {leftIcon}
            </div>
          </div>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          {...props}
        />
        
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <div className="text-agentic-text-muted">
              {rightIcon}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-agentic-accent-red">
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p className="mt-1 text-sm text-agentic-text-secondary">
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
